/* ----------------------------------------------------------------------------
 * SEO-D (W4a) data fetchers — the launch-critical SEO intelligence layer:
 * dashboard summary (gains/losses), prioritised opportunities, recommended
 * actions, and a read-only alerts inbox (+ resolve).
 *
 * Self-contained and OWNED by Agent SEO-D-FE (kept OUT of the shared
 * data/sources.ts, which is orchestrator-wired). Mirrors the W2a/W3a fetcher
 * discipline: gated behind USE_MOCK_DATA, honest nulls via numOrNull /
 * strOrNull, shapes matching the LOCKED W4a API contract EXACTLY.
 *
 * SAFETY: every W4a read is FREE + read-only over already-stored, workspace-
 * scoped rows — NO provider call, NO BudgetGuard, NO cache. Unlike the paid
 * W1a/W2a/W3a surfaces these are safe to AUTO-LOAD on mount; there is no
 * "click to fetch" spend guard because there is nothing to spend. The only
 * WRITE is resolving an alert (POST /v1/seo/alerts/{id}/resolve — EXISTING,
 * already live; not a new route).
 * -------------------------------------------------------------------------- */

import { api, USE_MOCK_DATA } from '@/lib/api';
import type { VitalSign, AttentionItem } from '@/types';
import type {
  SeoAlert,
  SeoAlertDetail,
  SeoAlertsResult,
  SeoAlertStatus,
  SeoAlertsState,
  SeoDashboardSummary,
  SeoExecSnapshot,
  SeoExecutiveSignal,
  SeoMovementState,
  SeoOrganicState,
  SeoOpportunitiesResult,
  SeoOpportunity,
  SeoOpportunityType,
  SeoOpportunityWindow,
  SeoOrganicSnapshot,
  SeoPrepareDraft,
  SeoRecommendedAction,
  SeoRecommendedActionsResult,
} from '@/types/seod';

/** Finite number or null — blank/absent/non-numeric all stay null. NEVER 0. */
function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** A COUNT that is a real, honest integer — absent/blank ⇒ 0 (a true zero, not
 *  a fabricated metric). Used ONLY for count fields the contract guarantees are
 *  present (gains/losses/open_alerts/tracked_*), never for provider metrics. */
function countOr0(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : 0;
}

/** Non-empty string or null — never coerces to a fabricated value. */
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

/** String or '' — for fields always present but occasionally blank. */
function strOrEmpty(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** Coerce the backend's honest movement_state to the known union. An absent or
 *  unrecognised value falls back to 'ok' (show the real count) — never invents a
 *  'no data' claim the backend did not make. */
function toMovementState(v: unknown): SeoMovementState {
  return v === 'no_data' || v === 'insufficient_history' || v === 'ok' || v === 'workspace'
    ? v
    : 'ok';
}

/** Coerce the backend's honest open-alerts state. Absent/unknown -> 'ok' (the
 *  count is trusted); only an explicit 'not_evaluated' claims no verdict. */
function toAlertsState(v: unknown): SeoAlertsState {
  return v === 'not_evaluated' ? 'not_evaluated' : 'ok';
}

/** Coerce the backend's honest organic data-state. Absent -> derived by the
 *  caller from the counts; an explicit no_data/expired/no_domain is preserved. */
function toOrganicState(v: unknown): SeoOrganicState | null {
  return v === 'ok' || v === 'no_data' || v === 'expired' || v === 'no_domain' ? v : null;
}

/* --- 1. Dashboard summary ---------------------------------------------------- */
// GET /v1/seo/dashboard-summary?days=30 (default 30, max 90). FREE, read-only.

const MOCK_DASHBOARD_SUMMARY: SeoDashboardSummary = {
  window: { start: '2026-06-17T00:00:00Z', end: '2026-07-17T00:00:00Z' },
  gains: 4,
  losses: 3,
  gains_detail: { entered_top10: 4 },
  losses_detail: { rank_drop: 2, left_top10: 1 },
  open_alerts: 5,
  alerts_state: 'ok',
  tracked_keywords: 42,
  tracked_projects: 2,
  movement_state: 'ok',
  source: 'bc_seo_alerts+bc_seo_rankings+ranked_organic_cache',
  fetched_at: '2026-07-17T06:30:00Z',
  organic: {
    total_organic_keywords: 50,
    page_one_keywords: 8,
    striking_distance_keywords: 14,
    top_actions: [
      { keyword: 'sound bath hackney', position: 13, target_page: 'https://omegahub.co.uk/sound-bath/', score: 71, action: 'Optimise the existing page for "sound bath hackney".' },
    ],
    refresh_gated: false,
    source: 'dataforseo.labs.ranked_keywords',
    fetched_at: '2026-07-17T06:30:00Z',
    state: 'ok',
  },
};

interface DashboardSummaryDto {
  window?: { start?: string | null; end?: string | null };
  gains?: number | string | null;
  losses?: number | string | null;
  gains_detail?: { entered_top10?: number | string | null };
  losses_detail?: { rank_drop?: number | string | null; left_top10?: number | string | null };
  open_alerts?: number | string | null;
  alerts_state?: string | null;
  tracked_keywords?: number | string | null;
  tracked_projects?: number | string | null;
  movement_state?: string | null;
  source?: string;
  fetched_at?: string | null;
  organic?: {
    total_organic_keywords?: number | string | null;
    page_one_keywords?: number | string | null;
    striking_distance_keywords?: number | string | null;
    top_actions?: Array<{ keyword?: string | null; position?: number | string | null; target_page?: string | null; score?: number | string | null; action?: string | null }>;
    refresh_gated?: boolean;
    source?: string | null;
    fetched_at?: string | null;
    state?: string | null;
  };
}

/** Map the ranked-organic snapshot honestly — counts are true integers,
 *  action metrics stay null when absent (never fabricated). */
function mapOrganic(o: DashboardSummaryDto['organic']): SeoOrganicSnapshot {
  const src = o ?? {};
  const actions = (src.top_actions ?? []).map((a) => ({
    keyword: strOrEmpty(a.keyword),
    position: numOrNull(a.position),
    target_page: strOrNull(a.target_page),
    score: numOrNull(a.score),
    action: strOrEmpty(a.action),
  }));
  const total = countOr0(src.total_organic_keywords);
  // Prefer the backend's explicit honest state; otherwise derive it from the
  // count (a true 0 with no state is 'no_data' — never fabricated as populated).
  const state: SeoOrganicState = toOrganicState(src.state) ?? (total > 0 ? 'ok' : 'no_data');
  return {
    total_organic_keywords: total,
    page_one_keywords: countOr0(src.page_one_keywords),
    striking_distance_keywords: countOr0(src.striking_distance_keywords),
    top_actions: actions,
    refresh_gated: src.refresh_gated === true,
    source: src.source ?? 'dataforseo.labs.ranked_keywords',
    fetched_at: src.fetched_at ?? null,
    state,
  };
}

/** Free workspace-level ranking gains/losses rollup. Safe on mount. */
export async function fetchSeoDashboardSummary(days = 30, projectId?: number | null): Promise<SeoDashboardSummary> {
  if (USE_MOCK_DATA) return MOCK_DASHBOARD_SUMMARY;
  const clamped = Math.min(Math.max(Math.trunc(days) || 30, 1), 90);
  const query: Record<string, string | number | undefined> = { days: clamped };
  // Thread the SELECTED 'Viewing' domain so figures scope to it (owner 2026-07-21
  // cross-domain-leakage fix); omitted -> honest workspace-wide.
  if (typeof projectId === 'number' && projectId > 0) query.project_id = projectId;
  const res = await api.get<DashboardSummaryDto>('/seo/dashboard-summary', query);
  return {
    window: {
      start: strOrEmpty(res.window?.start),
      end: strOrEmpty(res.window?.end),
    },
    gains: countOr0(res.gains),
    losses: countOr0(res.losses),
    gains_detail: { entered_top10: countOr0(res.gains_detail?.entered_top10) },
    losses_detail: {
      rank_drop: countOr0(res.losses_detail?.rank_drop),
      left_top10: countOr0(res.losses_detail?.left_top10),
    },
    open_alerts: countOr0(res.open_alerts),
    alerts_state: toAlertsState(res.alerts_state),
    tracked_keywords: countOr0(res.tracked_keywords),
    tracked_projects: countOr0(res.tracked_projects),
    movement_state: toMovementState(res.movement_state),
    source: res.source ?? 'bc_seo_alerts+bc_seo_rankings',
    fetched_at: res.fetched_at ?? null,
    organic: mapOrganic(res.organic),
  };
}

/* --- 2. Opportunities -------------------------------------------------------- */
// GET /v1/seo/opportunities?limit=50 (default 50, max 200). FREE, read-only.

const MOCK_OPPORTUNITIES: SeoOpportunitiesResult = {
  opportunities: [
    {
      type: 'ai_grounded',
      keyword: 'community growth platform',
      project_id: 1,
      domain: 'grow2gether.com',
      position: null,
      subject: 'Consolidate two thin pages competing for this term',
      body: 'Two URLs rank on page two for the same intent — merging them concentrates authority and is a near-term page-one candidate.',
      source: 'gsc',
      evidenced_at: '2026-07-16T06:30:00Z',
    },
    {
      type: 'striking_distance',
      keyword: 'online booking for therapists',
      project_id: 2,
      domain: 'booksesh.com',
      position: 11,
      subject: null,
      body: null,
      source: 'stored_rankings',
      evidenced_at: '2026-07-16T06:12:00Z',
    },
    {
      type: 'striking_distance',
      keyword: 'studio hire cardiff',
      project_id: 1,
      domain: 'grow2gether.com',
      position: 6,
      subject: null,
      body: null,
      source: 'stored_rankings',
      evidenced_at: '2026-07-16T06:12:00Z',
    },
  ],
  insights: [],
  ranked_organic_count: 0,
  ai_grounded_count: 1,
  insights_count: 0,
  striking_distance_count: 2,
  demand_count: 0,
  fetched_at: '2026-07-17T06:30:00Z',
};

interface OpportunityDto {
  type?: string | null;
  keyword?: string | null;
  project_id?: number | string | null;
  domain?: string | null;
  position?: number | string | null;
  subject?: string | null;
  body?: string | null;
  source?: string | null;
  evidenced_at?: string | null;
  volume?: number | string | null;
  traffic?: number | string | null;
  difficulty?: number | string | null;
  impressions?: number | string | null;
  target_page?: string | null;
  score?: number | string | null;
  action?: string | null;
  reasoning?: string | null;
  prepare_draft?: unknown;
  label?: string | null;
}

interface AuraInsightDto {
  project_id?: number | string | null;
  domain?: string | null;
  subject?: string | null;
  body?: string | null;
  source?: string | null;
  label?: string | null;
  evidenced_at?: string | null;
}

/** Narrow to a known opportunity type; anything unexpected is treated as
 *  striking_distance ONLY when the row is source stored_rankings, else the row
 *  keeps whatever the server said — we never relabel a source as AI. */
function normaliseOppType(t: unknown, source: string): SeoOpportunityType {
  if (
    t === 'ranked_organic' ||
    t === 'ai_grounded' ||
    t === 'striking_distance' ||
    t === 'demand_not_ranking'
  )
    return t;
  // Tolerate the BE's bucket alias for the GSC demand rows (machine label not
  // pinned in the locked contract — only the display name 'demand — not ranking yet').
  if (t === 'demand' || t === 'gsc_demand') return 'demand_not_ranking';
  if (source === 'ranked_organic_cache') return 'ranked_organic';
  return source === 'stored_rankings' ? 'striking_distance' : 'ai_grounded';
}

/** Parse the server's Prepare-draft affordance defensively; null when absent
 *  or malformed (never a fabricated action). */
function parsePrepareDraft(v: unknown): SeoPrepareDraft | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const req = (o.request && typeof o.request === 'object' ? o.request : {}) as Record<string, unknown>;
  const pay = (req.payload && typeof req.payload === 'object' ? req.payload : {}) as Record<string, unknown>;
  const endpoint = strOrNull(req.endpoint);
  const topic = strOrNull(pay.topic);
  if (endpoint !== '/v1/ai/recommend' || topic === null) return null;
  return {
    available: o.available === true,
    label: strOrEmpty(o.label) || 'Prepare draft',
    channel: strOrEmpty(o.channel) || 'blog',
    request: {
      method: strOrEmpty(req.method) || 'POST',
      endpoint,
      payload: {
        accept: pay.accept === true,
        channel: strOrEmpty(pay.channel) || 'blog',
        topic,
        project_id: numOrNull(pay.project_id) ?? 0,
      },
    },
  };
}

/** Free prioritised-opportunities read (AI-grounded insights + striking-distance
 *  band). Honest empty array when a workspace has neither. Safe on mount. */
export async function fetchSeoOpportunities(
  limit = 200,
  projectId?: number | null,
  window: SeoOpportunityWindow = '28d',
): Promise<SeoOpportunitiesResult> {
  if (USE_MOCK_DATA) return MOCK_OPPORTUNITIES;
  const clamped = Math.min(Math.max(Math.trunc(limit) || 50, 1), 200);
  const query: Record<string, string | number | undefined> = { limit: clamped };
  if (typeof projectId === 'number' && projectId > 0) query.project_id = projectId;
  // Selecting a window ONLY re-reads the already-stored rows for it (no sync).
  query.window = window;
  const res = await api.get<{
    opportunities?: OpportunityDto[];
    demand?: OpportunityDto[];
    insights?: AuraInsightDto[];
    ranked_organic_count?: number | string | null;
    ai_grounded_count?: number | string | null;
    insights_count?: number | string | null;
    striking_distance_count?: number | string | null;
    demand_count?: number | string | null;
    fetched_at?: string | null;
  }>('/seo/opportunities', query);
  const opportunities: SeoOpportunity[] = [...(res.opportunities ?? []), ...(res.demand ?? [])].map((o) => {
    const source = strOrNull(o.source) ?? 'stored_rankings';
    return {
      type: normaliseOppType(o.type, source),
      keyword: strOrNull(o.keyword),
      project_id: numOrNull(o.project_id),
      domain: strOrNull(o.domain),
      position: numOrNull(o.position),
      subject: strOrNull(o.subject),
      body: strOrNull(o.body),
      source,
      evidenced_at: strOrEmpty(o.evidenced_at),
      volume: numOrNull(o.volume),
      traffic: numOrNull(o.traffic),
      difficulty: numOrNull(o.difficulty),
      impressions: numOrNull(o.impressions),
      target_page: strOrNull(o.target_page),
      score: numOrNull(o.score),
      action: strOrNull(o.action),
      reasoning: strOrNull(o.reasoning),
      prepare_draft: parsePrepareDraft(o.prepare_draft),
      label: strOrNull(o.label),
    };
  });
  // "Aura Recommends" insights: aggregate GSC records with NO exact keyword.
  // Kept OUT of the keyword table (never a blank-keyword row); source preserved.
  const insights = (res.insights ?? []).map((n) => ({
    project_id: numOrNull(n.project_id),
    domain: strOrNull(n.domain),
    subject: strOrNull(n.subject),
    body: strOrNull(n.body),
    source: strOrNull(n.source) ?? 'gsc',
    label: strOrEmpty(n.label) || 'Aura Recommends',
    evidenced_at: strOrEmpty(n.evidenced_at),
  }));
  return {
    opportunities,
    insights,
    ranked_organic_count: countOr0(res.ranked_organic_count),
    ai_grounded_count: countOr0(res.ai_grounded_count),
    insights_count: countOr0(res.insights_count),
    striking_distance_count: countOr0(res.striking_distance_count),
    demand_count: countOr0(res.demand_count),
    fetched_at: res.fetched_at ?? null,
  };
}

/* --- 3. Recommended actions -------------------------------------------------- */
// GET /v1/seo/recommended-actions?status=open&limit=50. FREE, read-only.

const MOCK_RECOMMENDED_ACTIONS: SeoRecommendedActionsResult = {
  actions: [
    {
      alert_id: 101,
      alert_type: 'rank-drop',
      severity: 'warning',
      title: '“studio hire cardiff” dropped 4 positions',
      recommended_action:
        'Review recent on-page changes for “studio hire cardiff” and check for lost internal links — it fell from 6 to 10.',
      evidence: { keyword: 'studio hire cardiff', previous: 6, current: 10, checked_at: '2026-07-16T06:12:00Z' },
      created_at: '2026-07-16T06:15:00Z',
    },
    {
      alert_id: 102,
      alert_type: 'entered-top10',
      severity: 'info',
      title: '“therapist booking software” entered the top 10',
      recommended_action:
        'Reinforce the page now ranking 9th for “therapist booking software” with supporting content to push toward the top 5.',
      evidence: { keyword: 'therapist booking software', previous: 13, current: 9, checked_at: '2026-07-16T06:12:00Z' },
      created_at: '2026-07-16T06:15:00Z',
    },
  ],
  source: 'bc_seo_alerts+RecommendationEngine',
  fetched_at: '2026-07-17T06:30:00Z',
};

interface RecommendedActionDto {
  alert_id?: number | string | null;
  alert_type?: string | null;
  severity?: string | null;
  title?: string | null;
  recommended_action?: string | null;
  evidence?: {
    keyword?: string | null;
    previous?: number | string | null;
    current?: number | string | null;
    checked_at?: string | null;
  };
  created_at?: string | null;
}

/** Free recommended-actions read — each open alert mapped to deterministic,
 *  evidence-grounded action text. Safe on mount. */
export async function fetchSeoRecommendedActions(
  status: SeoAlertStatus = 'open',
  limit = 50,
  projectId?: number | null,
): Promise<SeoRecommendedActionsResult> {
  if (USE_MOCK_DATA) return MOCK_RECOMMENDED_ACTIONS;
  const clamped = Math.min(Math.max(Math.trunc(limit) || 50, 1), 200);
  const query: Record<string, string | number | undefined> = { status, limit: clamped };
  if (typeof projectId === 'number' && projectId > 0) query.project_id = projectId;
  const res = await api.get<{
    actions?: RecommendedActionDto[];
    source?: string;
    fetched_at?: string | null;
  }>('/seo/recommended-actions', query);
  const actions: SeoRecommendedAction[] = (res.actions ?? []).map((a) => ({
    alert_id: countOr0(a.alert_id),
    alert_type: strOrNull(a.alert_type) ?? 'unknown',
    severity: strOrNull(a.severity) ?? 'info',
    title: strOrEmpty(a.title),
    // Contract guarantees a non-empty string (generic fallback server-side); if
    // the server ever omits it, degrade to an honest generic prompt, never blank.
    recommended_action: strOrNull(a.recommended_action) ?? 'Review this alert.',
    evidence: {
      keyword: strOrEmpty(a.evidence?.keyword),
      previous: numOrNull(a.evidence?.previous),
      current: numOrNull(a.evidence?.current),
      checked_at: strOrEmpty(a.evidence?.checked_at),
    },
    created_at: strOrEmpty(a.created_at),
  }));
  return {
    actions,
    source: res.source ?? 'bc_seo_alerts+RecommendationEngine',
    fetched_at: res.fetched_at ?? null,
  };
}

/* --- 4. Alerts inbox (EXISTING GET /v1/seo/alerts) + resolve ----------------- */
// GET /v1/seo/alerts?status=open&limit=50 — existing AlertsController::list.
// POST /v1/seo/alerts/{id}/resolve — existing AlertsController::resolve.

const MOCK_ALERTS: SeoAlertsResult = {
  alerts: [
    {
      id: 101,
      project_id: 1,
      alert_type: 'rank-drop',
      severity: 'warning',
      title: '“studio hire cardiff” dropped 4 positions',
      detail: { keyword: 'studio hire cardiff', previous: 6, current: 10, checked_at: '2026-07-16T06:12:00Z' },
      status: 'open',
      created_at: '2026-07-16T06:15:00Z',
    },
    {
      id: 103,
      project_id: 2,
      alert_type: 'left-top10',
      severity: 'warning',
      title: '“therapist near me” fell out of the top 10',
      detail: { keyword: 'therapist near me', previous: 9, current: 14, checked_at: '2026-07-16T06:12:00Z' },
      status: 'open',
      created_at: '2026-07-16T06:15:00Z',
    },
  ],
};

interface AlertDto {
  id?: number | string | null;
  project_id?: number | string | null;
  alert_type?: string | null;
  severity?: string | null;
  title?: string | null;
  detail?: unknown;
  status?: string | null;
  created_at?: string | null;
}

/** Parse an alert's detail_json honestly — every field optional / null-honest. */
function parseAlertDetail(detail: unknown): SeoAlertDetail {
  const d = (detail && typeof detail === 'object' ? detail : {}) as Record<string, unknown>;
  return {
    keyword: strOrNull(d['keyword']),
    previous: numOrNull(d['previous'] ?? d['previous_position']),
    current: numOrNull(d['current'] ?? d['current_position'] ?? d['position']),
    checked_at: strOrNull(d['checked_at']),
  };
}

function narrowStatus(v: unknown): SeoAlertStatus {
  return v === 'acknowledged' || v === 'resolved' ? v : 'open';
}

/** Free read of the workspace's stored alerts (bc_seo_alerts). Safe on mount. */
export async function fetchSeoAlerts(
  status: SeoAlertStatus = 'open',
  limit = 50,
): Promise<SeoAlertsResult> {
  if (USE_MOCK_DATA) {
    return { alerts: MOCK_ALERTS.alerts.filter((a) => a.status === status).slice(0, limit) };
  }
  const clamped = Math.min(Math.max(Math.trunc(limit) || 50, 1), 200);
  const res = await api.get<{ alerts?: AlertDto[] }>('/seo/alerts', { status, limit: clamped });
  const alerts: SeoAlert[] = (res.alerts ?? []).map((a) => ({
    id: countOr0(a.id),
    project_id: numOrNull(a.project_id),
    alert_type: strOrNull(a.alert_type) ?? 'unknown',
    severity: strOrNull(a.severity) ?? 'info',
    title: strOrEmpty(a.title),
    detail: parseAlertDetail(a.detail),
    status: narrowStatus(a.status),
    created_at: strOrEmpty(a.created_at),
  }));
  return { alerts };
}

/** Resolve an alert (EXISTING write; owner/operator-gated server-side). */
export async function resolveSeoAlert(alertId: number): Promise<{ resolved: boolean; id: number }> {
  if (USE_MOCK_DATA) return { resolved: true, id: alertId };
  const res = await api.post<{ resolved?: boolean; id?: number | string | null }>(
    `/seo/alerts/${encodeURIComponent(String(alertId))}/resolve`,
  );
  return { resolved: res.resolved === true, id: countOr0(res.id) };
}

/* --- 5. Executive dashboard signal (composed FE-side) ------------------------ */
// A dashboard-ready projection of the FREE dashboard-summary + recommended-
// actions reads into the Executive OS VitalSign / AttentionItem shapes. NOT a
// new endpoint — the orchestrator wires this into fetchExecutiveOverview()
// (shared sources.ts) via a settle() call, so the workspace dashboard gets a
// live, module_scope 'seo' SEO signal. Entitlement-aware once bc_entitlements
// enforcement lands; today it is module_scope-scoped like the rest of SEO.

/** Map an alert severity to an Executive OS attention severity. */
function attentionSeverity(sev: string): 'low' | 'medium' | 'high' {
  const s = sev.toLowerCase();
  if (s === 'critical' || s === 'high' || s === 'error') return 'high';
  if (s === 'warning' || s === 'medium') return 'medium';
  return 'low';
}

/** Compose the SEO executive signal for the Main Executive OS from the two free
 *  reads. Scopes to the SELECTED 'Viewing' domain when a projectId is passed
 *  (owner 2026-07-21 cross-domain fix); omitted -> honest workspace-wide. Degrades
 *  honestly: every figure carries a state so the FE distinguishes a true zero
 *  from missing data / insufficient history — NEVER 0-as-missing, never blank.
 *  The dedicated Exec-OS SEO section renders the `seo` snapshot; the top-3
 *  ranked-organic actions ALSO seed the Opportunities ledger (suggested next
 *  moves) so it is populated from real ranked-organic data. */
export async function fetchSeoExecutiveSignal(projectId?: number | null): Promise<SeoExecutiveSignal> {
  const vitals: VitalSign[] = [];
  const attention: AttentionItem[] = [];
  let seo: SeoExecSnapshot | null = null;

  // Both reads are free; fetch in parallel and degrade each independently.
  const [summaryRes, actionsRes] = await Promise.allSettled([
    fetchSeoDashboardSummary(30, projectId),
    fetchSeoRecommendedActions('open', 50, projectId),
  ]);

  if (summaryRes.status === 'fulfilled') {
    const s = summaryRes.value;
    const o = s.organic;
    const organicState: SeoOrganicState = o.state ?? (o.total_organic_keywords > 0 ? 'ok' : 'no_data');
    seo = {
      totalOrganicKeywords: o.total_organic_keywords,
      pageOneKeywords: o.page_one_keywords,
      strikingDistanceKeywords: o.striking_distance_keywords,
      organicState,
      gains: s.gains,
      losses: s.losses,
      movementState: s.movement_state,
      openAlerts: s.open_alerts,
      alertsState: s.alerts_state,
      topActions: o.top_actions,
      trackedKeywords: s.tracked_keywords,
      trackedProjects: s.tracked_projects,
      fetchedAt: s.fetched_at,
    };

    // A ranking LOSS risk is honest only when the movement figure itself is
    // real (ok / workspace). no_data / insufficient_history => no claim.
    const movementReal = s.movement_state === 'ok' || s.movement_state === 'workspace';
    if (movementReal && s.losses > 0) {
      attention.push({
        id: 'seo_losses',
        kind: 'risk',
        title: `${s.losses} ranking ${s.losses === 1 ? 'loss' : 'losses'} in the last 30 days`,
        detail: `${s.losses_detail.rank_drop} rank drop${s.losses_detail.rank_drop === 1 ? '' : 's'} and ${s.losses_detail.left_top10} top-10 exit${s.losses_detail.left_top10 === 1 ? '' : 's'} across ${s.tracked_keywords} tracked keywords.`,
        engineKey: 'seo',
        href: '/seo/executive',
        severity: 'medium',
      });
    }

    // Suggested next moves: the top-3 ranked-organic actions from the shared
    // selector become Opportunities-ledger rows (real data -> the ledger is no
    // longer empty). Only when the organic snapshot is genuinely populated.
    if (organicState === 'ok') {
      for (const a of o.top_actions.slice(0, 3)) {
        if (!a.action) continue;
        const pos = typeof a.position === 'number' ? ` (currently #${a.position})` : '';
        attention.push({
          id: `seo_move_${a.keyword || a.action}`.slice(0, 96),
          kind: 'opportunity',
          title: a.keyword ? `Improve "${a.keyword}"${pos}` : a.action,
          detail: a.action,
          engineKey: 'seo',
          href: '/seo/executive',
          severity: 'low',
        });
      }
    }
  }

  if (actionsRes.status === 'fulfilled') {
    // Surface the single highest-severity recommended action as an attention
    // item — evidence-backed, links into the executive dashboard.
    const top = [...actionsRes.value.actions].sort(
      (a, b) => rankSev(b.severity) - rankSev(a.severity),
    )[0];
    if (top && attentionSeverity(top.severity) === 'high') {
      attention.push({
        id: `seo_action_${top.alert_id}`,
        kind: top.alert_type === 'entered-top10' ? 'opportunity' : 'risk',
        title: top.title || 'SEO alert needs attention',
        detail: top.recommended_action,
        engineKey: 'seo',
        href: '/seo/executive',
        severity: attentionSeverity(top.severity),
      });
    }
  }

  // If BOTH free reads failed, rethrow so the composer records a real failure
  // (mirrors fetchExecutiveOverview's "every sub-source failed" discipline).
  if (summaryRes.status === 'rejected' && actionsRes.status === 'rejected') {
    throw summaryRes.reason ?? actionsRes.reason ?? new Error('SEO signal unavailable.');
  }

  return { vitals, attention, seo };
}

/** Numeric rank for severity ordering (higher = more urgent). */
function rankSev(sev: string): number {
  const s = sev.toLowerCase();
  if (s === 'critical' || s === 'high' || s === 'error') return 3;
  if (s === 'warning' || s === 'medium') return 2;
  return 1;
}
